import { Empty, Flex, Timeline, Typography } from 'antd'
import { ArrowRight } from 'lucide-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { DateTimeCell } from 'src/components'

import { CATEGORY_COLORS, EVENT_KIND_COLORS } from './constants'
import { useStyles } from './styles'
import type { GraphNode, LiveEvent } from './types'

const { Text } = Typography

type Props = {
  source: GraphNode | null
  target: GraphNode | null
  events: LiveEvent[]
  onClose: () => void
}

export const EdgeDetailPanel = ({ source, target, events, onClose }: Props) => {
  const { styles } = useStyles()
  const { t } = useTranslation()

  const open = Boolean(source && target)

  useEffect(() => {
    if (!open) return undefined
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)

    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <aside className={styles.detail}>
      {source && target ? (
        <>
          <button
            type="button"
            className={styles.detailClose}
            onClick={onClose}
            aria-label={t('workspaceOverview.companyGraph.close')}
          >
            ×
          </button>

          <div className={styles.edgeHead}>
            <span className={styles.edgeEndpoint}>
              <span
                className={styles.detailDot}
                style={{ background: CATEGORY_COLORS[source.kind] }}
                aria-hidden
              />
              {source.label}
            </span>
            <ArrowRight
              size={14}
              style={{ color: 'var(--ant-color-text-tertiary)' }}
            />
            <span className={styles.edgeEndpoint}>
              <span
                className={styles.detailDot}
                style={{ background: CATEGORY_COLORS[target.kind] }}
                aria-hidden
              />
              {target.label}
            </span>
          </div>

          <div className={styles.detailSection}>
            <div className={styles.sectionLabel}>
              {t('workspaceOverview.companyGraph.liveEvents')}
            </div>
            {events.length > 0 ? (
              <Timeline
                items={events.map((ev) => ({
                  key: ev.id,
                  color: EVENT_KIND_COLORS[ev.kind],
                  children: (
                    <Flex vertical gap={1}>
                      <Text style={{ fontSize: 12.5 }}>
                        {source.label} {ev.text}
                      </Text>
                      <DateTimeCell
                        value={ev.ts}
                        variant="relative"
                        className={styles.timelineWhen}
                      />
                    </Flex>
                  )
                }))}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('workspaceOverview.companyGraph.noEdgeEvents')}
              />
            )}
          </div>
        </>
      ) : null}
    </aside>
  )
}
